/**
 * RoomBlockHatch (PPL-190) — the quiet "block {name}" exit hatch for rooms.
 *
 * Renders as a low-key footer affordance on the exit surfaces (RoomEnd,
 * RoomSummary, RoomDetail-ended) — deliberately NOT in the live room header:
 * step-aside / check-in / end are the in-room pressure valves; block appears
 * once the person has chosen to leave.
 *
 * Two-step confirm; copy is honest that the other side is never told (the
 * server closes any live room via the ordinary delete-for-both path, PPL-189,
 * so a block is indistinguishable from a normal closure). Idempotent: if the
 * caller already blocked them it renders a static "blocked" state.
 *
 * Identity register: personal — rooms use first names.
 */

import React, { useEffect, useState } from 'react';
import { Slash } from 'react-feather';
import { blockUser, checkBlockStatus } from '../services/messageService';

interface RoomBlockHatchProps {
  /** The other participant's uid. */
  otherUid: string;
  /** The other participant's display first name (personal register). */
  otherName: string;
  /** Called after a successful block (e.g. navigate to /rooms). */
  onBlocked?: () => void;
}

const RoomBlockHatch: React.FC<RoomBlockHatchProps> = ({ otherUid, otherName, onBlocked }) => {
  const [alreadyBlocked, setAlreadyBlocked] = useState<boolean | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!otherUid) return undefined;
    let cancelled = false;
    checkBlockStatus(otherUid)
      .then((s) => { if (!cancelled) setAlreadyBlocked(s.currentUserBlockedOther); })
      .catch(() => { if (!cancelled) setAlreadyBlocked(false); });
    return () => { cancelled = true; };
  }, [otherUid]);

  const handleBlock = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await blockUser(otherUid);
      setAlreadyBlocked(true);
      setConfirming(false);
      onBlocked?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "couldn't block right now — try again.");
    } finally {
      setBusy(false);
    }
  };

  // Placeholder invitees have no uid ("" on the room doc) — nothing to block.
  if (!otherUid) return null;
  if (alreadyBlocked === null) return null;

  if (alreadyBlocked) {
    return (
      <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'rgba(232,220,250,0.4)' }}>
        <Slash size={11} style={{ verticalAlign: '-1px', marginRight: 5 }} />
        {otherName} is blocked
      </div>
    );
  }

  if (!confirming) {
    return (
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          style={{
            appearance: 'none', cursor: 'pointer', border: 0, background: 'none',
            fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
            color: 'rgba(232,220,250,0.45)',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}
        >
          block {otherName}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 20,
        padding: '13px 15px',
        borderRadius: 14,
        background: 'rgba(239,68,68,0.06)',
        border: '1px solid rgba(239,68,68,0.22)',
        maxWidth: 420,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <div style={{ fontSize: 12.5, color: 'rgba(240,232,250,0.85)', lineHeight: 1.55 }}>
        block {otherName}? they won&apos;t be able to reach you or invite you to rooms — and
        they won&apos;t be told.
      </div>
      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={busy}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
            flex: 1, padding: '8px', borderRadius: 10,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(240,232,250,0.85)', fontSize: 12.5, fontWeight: 600,
          }}
        >
          cancel
        </button>
        <button
          type="button"
          onClick={() => void handleBlock()}
          disabled={busy}
          style={{
            appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', border: 0,
            flex: 1, padding: '8px', borderRadius: 10,
            background: 'rgba(239,68,68,0.85)',
            color: '#fff', fontSize: 12.5, fontWeight: 700,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'blocking…' : `block ${otherName}`}
        </button>
      </div>
    </div>
  );
};

export default RoomBlockHatch;
